/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { BrowserCacheLocation, LogLevel } from '@azure/msal-browser'
import { enviroment } from './enviroment'

// Set by a deliberate logout (useNavigationBarViewModel.logout) and read by
// UnauthenticatedView (App.tsx) so the app shows a signed-out screen instead of
// auto-starting a new login. Stored in localStorage: Safari drops
// sessionStorage across the cross-site logout round-trip (the marker vanished
// and users bounced straight back in), while localStorage survives. Cleared on
// any non-'/' landing, before the Sign in redirect, and on authenticated boot.
export const JUST_LOGGED_OUT_KEY = 'justLoggedOut'

// MSAL diagnostics: verbose console logging on the DEV tenant only. The CI dev
// build sets REACT_APP_AUTHORITY_DOMAIN to jskitmakerdev.b2clogin.com (see
// .github/workflows/develop_app-kitmaker-dev.yml), so prod stays quiet without
// any extra configuration. Used to capture the actual MSAL error codes behind
// the Safari auth issues.
const msalVerboseLog = enviroment.authorityDomain === 'jskitmakerdev.b2clogin.com'

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1_susi'
  },
  authorities: {
    signUpSignIn: {
      authority: enviroment.authority
    }
  },
  authorityDomain: enviroment.authorityDomain
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: enviroment.clientId, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      // Without an explicit logLevel, msal filters everything above Info away
      // BEFORE the callback runs - Verbose diagnostics require both.
      logLevel: msalVerboseLog ? LogLevel.Verbose : LogLevel.Warning,
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii || !msalVerboseLog) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
        }
      }
    }
  }
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    `${enviroment.scopePrefix}/Web.Read`,
    `${enviroment.scopePrefix}/Web.Write`,
  ]
}
